/*global google*/
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography } from 'antd';
import { Client } from '@stomp/stompjs';
import { useTranslation } from 'react-i18next';
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { getStoreList } from 'store/requests/definitions';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';
import { getCourierTrackingDetails } from '../../../store/requests/courier';
import { LIVE_SCREEN_EXECUTIVE, MAP_DEFAULT_VALUES } from 'helpers/constants';

const { Text } = Typography;

let markersPayload = {};

const LiveScreen = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const map = useRef();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const activeStoreId = useSelector(
    (state) => state.auth?.user?.activeStore?.id,
  );
  const activeRegionId = useSelector(
    (state) => state.auth?.user?.activeRegion?.id,
  );
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [selectedStoreMarker, setSelectedStoreMarker] = useState(null);

  const onMapLoaded = (loadedMap) => {
    map.current = loadedMap;
    defaultMapPosition();
  };
  const defaultMapPosition = (zoom = MAP_DEFAULT_VALUES.ZOOM) => {
    if (!map.current) {
      return;
    }

    map.current.setZoom(zoom);
    map.current.setCenter({
      lat: MAP_DEFAULT_VALUES.LAT,
      lng: MAP_DEFAULT_VALUES.LNG,
    });
  };

  useEffect(() => {
    if (stores.length > 0) {
      if (window.google && window.google.maps) {
        if (map.current && map.current instanceof window.google.maps.Map) {
          const bounds = new window.google.maps.LatLngBounds();

          stores.forEach((store) => {
            if (store.latitude && store.longitude) {
              bounds.extend(
                new window.google.maps.LatLng(store.latitude, store.longitude),
              );
            }
          });

          if (!bounds.isEmpty()) {
            map.current.fitBounds(bounds);
            const maxZoom = MAP_DEFAULT_VALUES.MAX_ZOOM;
            const currentZoom = map.current.getZoom();
            map.current.setZoom(Math.min(currentZoom, maxZoom));
          }
        }
      }
    }
  }, [stores, map]);

  useEffect(() => {
    const client = new Client({
      brokerURL: `wss://${window.location.host}/websocket/live-feed`,
      reconnectDelay: 10000,
      heartbeatOutgoing: 45000,
      heartbeatIncoming: 45000,
    });

    client.onConnect = function (frame) {
      client.subscribe(
        '/user/queue/live-feed-gps-stream',
        function (message) {
          const payload = JSON.parse(message.body);
          if (!payload.latitude || !payload.longitude) {
            return;
          }
          const { latitude, longitude } = payload;

          if (typeof markersPayload[payload.plateNumber] != 'undefined') {
            const markerPosition = new window.google.maps.LatLng(
              parseFloat(latitude),
              parseFloat(longitude),
            );
            markersPayload[payload.plateNumber].setPosition(markerPosition);
          } else {
            const marker = new window.google.maps.Marker({
              position: {
                lat: parseFloat(latitude),
                lng: parseFloat(longitude),
              },
              icon: { url: '/static/img/green-record.svg' },
              map: map.current,
              label: {
                text: `${payload.phoneNumber} - ${payload.plateNumber}`,
                fontSize: '12px',
                fontWeight: 'bold',
              },
            });

            marker.addListener('click', handleMarkerClick(payload));
            markersPayload[payload.plateNumber] = marker;
          }
        },
        { 'auto-delete': true },
      );
    };

    client.onStompError = function (frame) {
      console.log('Broker reported error: ');
      console.log(frame.headers['message']);
      console.log('Additional details: ', frame.body);
      client.deactivate();
    };

    client.activate();

    getStoreList(
      activeStoreId
        ? `filter=id==${activeStoreId}`
        : `filter=region.id==${activeRegionId}`,
    ).then(async (storesRes) => {
      const storeInfo = storesRes.content;
      setStores(storeInfo);
    });

    return () => {
      client.deactivate();
    };
  }, []);

  const handleMarkerClick = (payload) => () => {
    if (payload.phoneNumber) {
      getCourierTrackingDetails(payload.phoneNumber).then((res) =>
        setSelectedMarker({ ...payload, ...res }),
      );
    }
  };

  return (
    <div className="liveScreenWrapper">
      <Modal
        title={t('info')}
        open={loading}
        okText={t('ok')}
        okType="danger"
        onOk={() => setLoading(false)}
        onCancel={() => setLoading(false)}
        width={400}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        {t('loadingLiveFeed')}
      </Modal>
      <GoogleMap
        onLoad={onMapLoaded}
        mapContainerStyle={{
          width: '100%',
          height: '81vh',
        }}
      >
        {stores &&
          stores.map((store, index) => (
            <Marker
              key={index}
              position={{
                lat: parseFloat(store.latitude),
                lng: parseFloat(store?.longitude),
              }}
              icon="/static/img/ceptesokpin.svg"
              onClick={() => setSelectedStoreMarker(store)}
            />
          ))}
        {selectedMarker && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedMarker?.latitude),
              lng: parseFloat(selectedMarker?.longitude),
            }}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div>
              <p>
                <Text strong>{t('fullName')}:</Text> {selectedMarker?.fullName}
              </p>
              <p>
                <Text strong>{t('store')}:</Text> {selectedMarker?.storeCode} /{' '}
                {selectedMarker?.storeName}
              </p>
              <p>
                <Text strong>{t('phoneNumber')}:</Text>{' '}
                {selectedMarker?.phoneNumber}
              </p>
              <p>
                <Text strong>{t('plateNumber')}:</Text>{' '}
                {selectedMarker?.vehiclePlate}
              </p>
              <p>
                <Text strong>{t('numberOfOrdersCarried')}:</Text>
                {selectedMarker?.orderCount} /{' '}
                {selectedMarker?.numberOfOrdersProcessed}
              </p>
              <p>
                <Text strong>{t('distributionStartTime')}:</Text>{' '}
                {new Date(selectedMarker?.courierTransferTime)
                  .toLocaleString('tr-TR', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                  .replace(',', '')}
              </p>
            </div>
          </InfoWindow>
        )}
        {selectedStoreMarker && (
          <InfoWindow
            position={{
              lat: parseFloat(selectedStoreMarker?.latitude),
              lng: parseFloat(selectedStoreMarker?.longitude),
            }}
            onCloseClick={() => setSelectedStoreMarker(null)}
          >
            <div>
              <p>
                <Text strong>{t('storeCode')}:</Text>{' '}
                {selectedStoreMarker?.code}
              </p>
              <p>
                <Text strong>{t('storeName')}:</Text>{' '}
                {selectedStoreMarker?.name}
              </p>
              <p>
                <Text strong>{t('storePhoneNumber')}:</Text>{' '}
                {selectedStoreMarker?.phoneNumber || '--'}
              </p>
              <p>
                <Text strong>{t('branchManager')}:</Text>{' '}
                {selectedStoreMarker?.region?.userRoles?.find(
                  (userRole) =>
                    userRole.role === LIVE_SCREEN_EXECUTIVE.BRANCH_MANAGER &&
                    userRole.active,
                )?.user?.nameSurname || '--'}
              </p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};

export default LiveScreen;
